import * as React from "react";
import { Rating, Stack, Typography } from "@mui/material";

import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../hooks/useXoraia";
import { TUseXElement, useXElement } from "../../hooks/useXElement";
import { USE_SELECTED_INDEX } from "../../hooks/useSelectedElement";
import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../lib/clickHandler";
import { XElementConfig } from "../../utils/interfaces/Elements/XElement.interfaces";

import { isEqual } from "lodash";
import {
  IXRatingProps,
  XRatingProps,
} from "../../utils/interfaces/Elements/XRating.interfaces";
import { generateSide } from "../../lib/generateSide";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XRating: React.FC<XRatingProps> = React.memo(
  ({ config = {} as XElementConfig }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env } = useXoraiaValue();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const { xElement } = useXElement<TUseXElement<IXRatingProps>>(config.index);

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    if (!xElement || Object.keys(xElement).length === 0) return null;

    // WE GET ALL DEVICES PROPS AS VALUE
    const { value } = xElement;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXRatingProps>(
      value as any,
      breakpoint
    );

    const devProps = {
      id: config.index,
      onClick: (e: any) => clickHandler(e, setSelectedElement, config.index),
    };

    const {
      editable,
      initialRate,
      reviewCount = env === "dev" ? 1 : 0,
      reviewCountMode,
      color,
      bgColor,
      fontSize,
      fontFamily,
      margin,
      padding,
      textAlign,
      fontWeight,
      width,
      height,
      ...sx
    } = valueAtBreakPoint;

    const hasReview =
      reviewCountMode &&
      reviewCountMode !== "none" &&
      reviewCount &&
      reviewCount > 0;

    let rating = (
      <Rating
        {...(env === "dev" && editable && !hasReview && devProps)}
        readOnly={!editable}
        size="small"
        precision={0.5}
        defaultValue={initialRate}
        sx={{
          ...sx,
          color: color,
          bgcolor: bgColor,
          padding: !hasReview ? generateSide("padding", padding) : undefined,
          margin: !hasReview ? generateSide("margin", margin) : undefined,
        }}
      />
    );

    if (!editable && env === "dev") {
      rating = (
        <Stack
          {...(!hasReview && devProps)}
          sx={{
            display: "inline-block",
            height: "max-content",
            width: "max-content",
          }}
        >
          {rating}
        </Stack>
      );
    }

    if (hasReview) {
      rating = (
        <Stack
          {...(env === "dev" && devProps)}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            padding: generateSide("padding", padding),
            margin: generateSide("margin", margin),
            width,
            height,
          }}
        >
          {rating}
          <Typography
            component="span"
            fontSize={fontSize}
            fontWeight={fontWeight}
            textAlign={textAlign}
            fontFamily={fontFamily}
          >
            {reviewCountMode === "full"
              ? `(${reviewCount} Avis)`
              : `(${reviewCount})`}
          </Typography>
        </Stack>
      );
    }

    return rating;
  },
  compareProps
);

export default XRating;
